<script>
import {getUrlParameter} from "@/baseUtils";
import i18n from "@/i18n";
import {mapActions, mapState} from "pinia";
import {mainSearchStore} from "@/store/mainSearchStore";
import BaseTab from "@/components/BaseTab.vue";
import Carousel from 'primevue/carousel';
import Dropdown from 'primevue/dropdown';

export default {
  name: "MainSearchControls",
  components: {
    BaseTab,
    Carousel,
    Dropdown
  },
  data() {
    return {
      activeTab: { label: i18n.global.t('mainSearch.filters.everywhere'), code: 'ALL' },
      selectedOrderBy: null,
      selectedSearchBy: null
    }
  },
  computed: {
    ...mapState(mainSearchStore, [
      'searchParams',
      'hasSearchParamControls',
      'searchByOptions',
      'multipleSearch'
    ]),
    searchTabs() {
      return [
        { label: i18n.global.t('mainSearch.filters.everywhere'), code: 'ALL' },
        { label: i18n.global.t('mainSearch.filters.proposal'), code: 'PROPOSAL' },
        { label: i18n.global.t('mainSearch.filters.opinion'), code: 'OPINION' },
        { label: i18n.global.t('mainSearch.filters.argument'), code: 'ARGUMENT' },
        { label: i18n.global.t('mainSearch.filters.member'), code: 'MEMBER' },
        { label: i18n.global.t('mainSearch.filters.organization'), code: 'ORGANIZATION' },
        { label: i18n.global.t('mainSearch.filters.forum'), code: 'FORUM' },
        { label: i18n.global.t('mainSearch.filters.source'), code: 'SOURCE' },
      ]
    },
    orderByItems() {
      return [
        { name: i18n.global.t('mainSearch.orderByFilter.created'), code: 'CREATED' },
        { name: i18n.global.t('mainSearch.orderByFilter.updated'), code: 'UPDATED' },
        { name: i18n.global.t('mainSearch.orderByFilter.rated'), code: 'RATED' },
      ]
    }
  },
  watch: {
    selectedOrderBy: {
      handler(newVal) {
        this.updateSearchParams('searchOrder', newVal.code);
      }
    },
    selectedSearchBy: {
      handler(newVal) {
        this.updateSearchParams('searchMode', newVal ? newVal.code : null);

        if (!this.multipleSearch) {
          this.runSearch(false, true);
        }
      }
    },
    activeTab: {
      handler() {
        this.selectedOrderBy = this.orderByItems[0];
        this.selectedSearchBy = this.searchByOptions[0];
      }
    },
    "searchParams.searchType": {
      handler(val) {
        this.activeTab = this.searchTabs.filter((tab => tab.code === val))[0];
      }
    },
    "searchParams.searchMode": {
      handler(val) {
        this.selectedSearchBy = this.searchByOptions.filter((item => item.code === val))[0];
      }
    }
  },
  methods: {
    ...mapActions(mainSearchStore, [
      'updateSearchParams',
      'updateSearchUrl',
      'runSearch',
      'resetSearch',
    ]),
    setActiveTab(tab) {
      this.updateSearchParams('searchType', tab.code);

      this.selectedSearchBy = this.searchByOptions[0];
      this.updateSearchParams('searchMode', 'ALL');

      this.resetSearch(true, true);
      this.updateSearchUrl();

      this.activeTab = tab;
    },
  },
  beforeMount() {
    const searchString = getUrlParameter('q');
    const searchType = getUrlParameter('type');

    if (searchString) {
      this.updateSearchParams('searchString', searchString);
    }

    if (searchType) {
      this.updateSearchParams('searchType', searchType);
    } else {
      this.activeTab = this.searchTabs[0];
    }
  },
  mounted() {
    setTimeout( ()=> {
      this.selectedOrderBy = this.orderByItems.filter((item => item.code === this.searchParams.searchOrder))[0];
    }, 2000);
  }
}
</script>

<template>
  <div class="search-tabs">
    <Carousel :value="searchTabs" :numVisible="5" :numScroll="1" :showIndicators="false">
      <template #previcon>
        <div>
          <font-awesome-icon :icon="['fas', 'chevron-left']"/>
        </div>
      </template>
      <template #item="slotProps">
        <base-tab :key="slotProps.data.code"
                  :label="slotProps.data.label"
                  :active="slotProps.data.code === activeTab.code"
                  @click="setActiveTab(slotProps.data)"
        />
      </template>
      <template #nexticon>
        <div>
          <font-awesome-icon :icon="['fas', 'chevron-right']"/>
        </div>
      </template>
    </Carousel>
    <div v-if="hasSearchParamControls" class="search-param-controls">
      <div class="search-param-control">
        <span class="search-param-control__label">{{$t('mainSearch.orderByFilter.filterTitle')}}</span>
        <Dropdown v-model="selectedOrderBy"
                  :options="orderByItems"
                  optionLabel="name"
                  class="search-param-control__input"
        />
      </div>
      <div class="search-param-control">
        <span class="search-param-control__label">{{$t('mainSearch.searchByFilter.filterTitle')}}</span>
        <Dropdown v-model="selectedSearchBy"
                  :options="searchByOptions"
                  optionLabel="name"
                  class="search-param-control__input"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.search-tabs {
  max-width: 600px;
  margin-bottom: 10px;
  position: fixed;
  z-index: 2;
  width: 100%;
  background: #fff;
  box-shadow: 0 2px 4px 0 rgba(79, 79, 79, 0.15);

  &:before {
    content: "";
    position: absolute;
    top: -10px;
    width: 100%;
    height: 10px;
    background: #E9EAED;
  }

  .search-param-controls {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 15px;
    border-top: 1px solid #F2F2F2;

    .search-param-control {
      margin-right: 20px;
      .p-select {
        outline: none !important;
      }
      &__label {
        color: #4f4f4f;
      }
      &__input {
        border: none;
        .p-select-label {
          font-size: 14px;
          padding-left: 5px;
          padding-right: 5px;
          color: #7835a6;
        }
        .p-select-dropdown {
          width: 10px;
        }
      }
    }
  }


}

.p-select-list-container {
  box-shadow: 0 2px 8px 0 rgba(79, 79, 79, 0.5);
  .p-select-option {
    font-size: 12px !important;
    color: #4f4f4f !important;
    background: transparent !important;
    &.p-select-option-selected {
      background: transparent !important;
      color: #7835a6 !important;
    }
  }

}
</style>